"use client";

import React from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import * as Tooltip from "@radix-ui/react-tooltip";
import { Plus, Edit, FileText, Clock, User, Mail, Calendar, File } from "lucide-react";
import { FAB_POSITION_CLASSES } from "@/components/ui/fab-position";

// Standardized FAB icons for different actions
export const FAB_ICONS = {
  create: Plus,
  edit: Edit,
  compose: Mail,
  log: Clock,
  add: User,
  template: FileText,
  document: File,
  meeting: Calendar,
} as const;

export type FABIconType = keyof typeof FAB_ICONS;

interface StandardizedFABProps {
  onClick: () => void;
  /** Preset icon. Ignored when `icon` is provided. Defaults to the create (+) icon. */
  iconType?: FABIconType;
  /** Explicit icon node — overrides `iconType`. */
  icon?: React.ReactNode;
  label: string;
  className?: string;
  size?: "sm" | "md" | "lg";
  variant?: "default" | "primary" | "secondary";
  disabled?: boolean;
  showTooltip?: boolean;
  /**
   * Render as an EXTENDED FAB — icon plus a visible text label.
   *
   * Default for a reason: this button only renders on touch screens, where
   * there is no hover, so a hover tooltip on a bare "+" never appears and the
   * action is unlabelled in practice. A visible label removes the guesswork.
   * Pass `false` for a genuinely self-evident icon-only FAB.
   */
  extended?: boolean;
}

export function StandardizedFAB({
  onClick,
  iconType = "create",
  icon,
  label,
  className,
  size = "md",
  variant = "primary",
  disabled = false,
  showTooltip = true,
  extended = true,
}: StandardizedFABProps) {
  const sizeClasses = {
    sm: "h-12 w-12",
    md: "h-14 w-14",
    lg: "h-16 w-16",
  };

  // Extended variant: a pill sized by its label rather than a fixed circle.
  const extendedSizeClasses = {
    sm: "h-12 px-4 gap-2",
    md: "h-14 px-5 gap-2.5",
    lg: "h-16 px-6 gap-3",
  };

  // Use the shared brand tokens so the FAB is always visible. (A prior version
  // used `bg-brand-blue`, which is undefined in the Tailwind config and rendered
  // a white-on-white, only-the-outline-visible button.)
  const variantClasses = {
    default:
      "bg-card text-card-foreground shadow-lg hover:bg-accent border border-border",
    primary:
      "bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 border border-primary",
    secondary:
      "bg-secondary text-secondary-foreground shadow-lg hover:bg-secondary/90 border border-border",
  };

  const iconSizes = {
    sm: "h-4 w-4",
    md: "h-6 w-6",
    lg: "h-8 w-8",
  };

  const IconComponent = FAB_ICONS[iconType];

  const fabButton = (
    <Button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        // Stacked ABOVE the support-chat bubble (which is fixed to the same
        // corner at z-index 9999) and padded for the iOS/Android home indicator
        // and rounded screen corners. Mobile only — the FAB is md:hidden.
        FAB_POSITION_CLASSES,
        "z-50 rounded-full transition-all duration-200",
        "hover:scale-105 active:scale-95 shadow-xl",
        "md:hidden", // Only show on mobile — desktop uses the top "New" button
        "focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring",
        "touch-manipulation", // Better touch handling
        extended ? extendedSizeClasses[size] : cn("p-0", sizeClasses[size]),
        variantClasses[variant],
        className
      )}
      aria-label={label}
      // `title` is a hover affordance too, but it is also what a desktop
      // browser and some assistive tech read; harmless on touch.
      title={label}
    >
      <div className="flex items-center justify-center gap-2">
        {/* Icon inherits the button's text color (text-primary-foreground, etc.). */}
        {icon ?? <IconComponent className={cn(iconSizes[size], "text-current")} />}
        {extended && (
          <span className="whitespace-nowrap text-sm font-semibold">{label}</span>
        )}
      </div>
    </Button>
  );

  // An extended FAB already shows its label, so a tooltip would duplicate it.
  // And a tooltip on a touch-only element never opens in the first place, which
  // is why the label moved onto the button.
  if (!showTooltip || extended) {
    return fabButton;
  }

  // Self-provided TooltipProvider (TOOLTIP_SELF_PROVIDER): a bare
  // Tooltip.Root crashes wherever an ancestor provider is missing.
  return (
    <Tooltip.Provider>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>{fabButton}</Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            sideOffset={8}
            className="bg-gray-900 text-white px-3 py-2 rounded text-sm shadow z-[60]"
          >
            {label}
            <Tooltip.Arrow className="fill-gray-900" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}

// Pre-configured FAB components for common use cases
export const CreateTaskFAB = ({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled?: boolean;
}) => (
  <StandardizedFAB onClick={onClick} iconType="create" label="Create Task" disabled={disabled} />
);

export const CreateContactFAB = ({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled?: boolean;
}) => (
  <StandardizedFAB onClick={onClick} iconType="create" label="Create Contact" disabled={disabled} />
);

export const CreateTicketFAB = ({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled?: boolean;
}) => (
  <StandardizedFAB onClick={onClick} iconType="create" label="Create Ticket" disabled={disabled} />
);

export const LogTimeFAB = ({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) => (
  <StandardizedFAB onClick={onClick} iconType="log" label="Log Time" disabled={disabled} />
);

export const ComposeEmailFAB = ({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled?: boolean;
}) => (
  <StandardizedFAB onClick={onClick} iconType="compose" label="Compose Email" disabled={disabled} />
);

export const CreateTemplateFAB = ({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled?: boolean;
}) => (
  <StandardizedFAB
    onClick={onClick}
    iconType="template"
    label="Create Template"
    disabled={disabled}
  />
);

export const CreateSignatureFAB = ({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled?: boolean;
}) => (
  <StandardizedFAB
    onClick={onClick}
    iconType="create"
    label="Create Signature"
    disabled={disabled}
  />
);

export const CreateDocumentFAB = ({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled?: boolean;
}) => (
  <StandardizedFAB
    onClick={onClick}
    iconType="document"
    label="Create Document"
    disabled={disabled}
  />
);

export const AddUserFAB = ({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) => (
  <StandardizedFAB onClick={onClick} iconType="add" label="Add User" disabled={disabled} />
);
